import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Users, Scan, Printer, Settings, Search, Filter } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';

interface ReleveConfig {
  type: 'devoirs' | 'examens';
  niveau: string;
  option: string;
  semestre: string;
  matiere?: string;
  session?: 'ordinaire' | 'rattrapage';
}

interface EtudiantReleve {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  codeCopie?: string;
  notes: {
    devoir1?: number;
    devoir2?: number;
    examen?: number;
    moyenne?: number;
  };
  status: 'complete' | 'incomplete' | 'absent';
}

export const GenerationReleves: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedType, setSelectedType] = useState<'devoirs' | 'examens'>('devoirs');
  const [config, setConfig] = useState<ReleveConfig>({
    type: 'devoirs',
    niveau: '',
    option: '',
    semestre: '',
  });
  
  const { data: fetchedEtudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery({});
  const [relevesEtudiants, setRelevesEtudiants] = useState<EtudiantReleve[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // Transformer les étudiants en format relevé de notes
    if (Array.isArray(fetchedEtudiants)) {
      const releveEtudiants: EtudiantReleve[] = fetchedEtudiants.map(e => ({
        id: e.id,
        matricule: e.matricule,
        nom: e.nom,
        prenoms: e.prenoms,
        codeCopie: '', // À récupérer depuis l'API si disponible
        notes: {
          // devoir1: Math.random() * 20, // Remplacer par vraie valeur
          // devoir2: Math.random() * 20,
          // examen: Math.random() * 20,
          moyenne: 0 // Calculer à partir des vraies notes
        },
        status: 'incomplete' // Calculer selon les vraies notes
      }));
      
      setRelevesEtudiants(releveEtudiants);
      setLoading(false);
    }
  }, [fetchedEtudiants]);

  const handleGenerate = async () => {
    if (!config.niveau || !config.option || !config.semestre) {
      alert('Veuillez configurer tous les paramètres requis');
      return;
    }

    setGenerating(true);
    try {
      // Simuler la génération
      await new Promise(resolve => setTimeout(resolve, 2000));
      setShowPreview(true);
    } catch (error) {
      alert('Erreur lors de la génération des relevés');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = (type: 'all' | 'individual', etudiantId?: number) => {
    if (type === 'all') {
      alert('Impression de tous les relevés...');
    } else if (etudiantId) {
      const etudiant = relevesEtudiants.find(e => e.id === etudiantId);
      alert(`Impression du relevé de ${etudiant?.nom} ${etudiant?.prenoms}`);
    }
  };

  const handleExport = (format: 'pdf' | 'excel') => {
    alert(`Exportation des relevés en format ${format.toUpperCase()}...`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'complete':
        return <Badge variant="success">Complet</Badge>;
      case 'incomplete':
        return <Badge variant="warning">Incomplet</Badge>;
      case 'absent':
        return <Badge variant="danger">Absent</Badge>;
      default:
        return <Badge variant="default">Inconnu</Badge>;
    }
  };

  const filteredEtudiants = relevesEtudiants.filter(etudiant =>
    etudiant.matricule.toLowerCase().includes(searchQuery.toLowerCase()) ||
    etudiant.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
    etudiant.prenoms.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Génération de Relevés de Notes</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Génération et impression des relevés selon le cahier des charges</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" icon={<Settings size={20} />}>
            Paramètres
          </Button>
          <Button variant="primary" icon={<FileText size={20} />} onClick={handleGenerate} disabled={generating}>
            {generating ? <Spinner size="sm" /> : <Download size={20} />}
            {generating ? 'Génération...' : 'Générer'}
          </Button>
        </div>
      </div>

      {/* Type de Relevé */}
      <Card>
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Type de Relevé</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedType === 'devoirs'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}
            onClick={() => setSelectedType('devoirs')}
          >
            <div className="flex items-center mb-3">
              <Users className="text-blue-600 dark:text-blue-400 mr-2" size={24} />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Relevés de Devoirs</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Générés à partir de la liste des étudiants par option et niveau.
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">Inclus:</span> Notes de devoirs, moyennes, coefficients
            </div>
          </div>

          <div
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedType === 'examens'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}
            onClick={() => setSelectedType('examens')}
          >
            <div className="flex items-center mb-3">
              <Scan className="text-green-600 dark:text-green-400 mr-2" size={24} />
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">Relevés d'Examens</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Générés à partir des copies d'examens scannées et associées.
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">Inclus:</span> Notes d'examens, codes copies, sessions
            </div>
          </div>
        </div>
      </Card>

      {/* Configuration */}
      <Card>
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Configuration</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Niveau</label>
            <select
              value={config.niveau}
              onChange={(e) => setConfig({...config, niveau: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Sélectionner...</option>
              <option value="L1">L1</option>
              <option value="L2">L2</option>
              <option value="L3">L3</option>
              <option value="M1">M1</option>
              <option value="M2">M2</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Option</label>
            <select
              value={config.option}
              onChange={(e) => setConfig({...config, option: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Sélectionner...</option>
              <option value="Maintenance Industrielle">Maintenance Industrielle</option>
              <option value="BTP">BTP</option>
              <option value="Informatique">Informatique</option>
              <option value="Commerce">Commerce</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Semestre</label>
            <select
              value={config.semestre}
              onChange={(e) => setConfig({...config, semestre: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Sélectionner...</option>
              <option value="1">Semestre 1</option>
              <option value="2">Semestre 2</option>
              <option value="3">Semestre 3</option>
              <option value="4">Semestre 4</option>
              <option value="5">Semestre 5</option>
              <option value="6">Semestre 6</option>
            </select>
          </div>

          {selectedType === 'examens' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Session</label>
              <select
                value={config.session}
                onChange={(e) => setConfig({...config, session: e.target.value as 'ordinaire' | 'rattrapage'})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="ordinaire">Ordinaire</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
            </div>
          )}
        </div>
      </Card>

      {/* Résultats de la génération */}
      {showPreview && (
        <Card>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Aperçu des Relevés</h3>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" icon={<Download size={16} />} onClick={() => handleExport('pdf')}>
                  Export PDF
                </Button>
                <Button variant="outline" size="sm" icon={<Download size={16} />} onClick={() => handleExport('excel')}>
                  Export Excel
                </Button>
                <Button variant="primary" size="sm" icon={<Printer size={16} />} onClick={() => handlePrint('all')}>
                  Imprimer Tous
                </Button>
              </div>
            </div>
          </div>

          {/* Recherche */}
          <div className="mb-4">
            <div className="relative">
              <Search
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <Input
                placeholder="Rechercher un étudiant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
              <div className="text-center">
                <p className="text-sm text-blue-600 dark:text-blue-400">Total Étudiants</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{filteredEtudiants.length}</p>
              </div>
            </Card>
            <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700">
              <div className="text-center">
                <p className="text-sm text-green-600 dark:text-green-400">Complets</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                  {filteredEtudiants.filter(e => (e as any).status === 'complete').length}
                </p>
              </div>
            </Card>
            <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
              <div className="text-center">
                <p className="text-sm text-yellow-600 dark:text-yellow-400">Incomplets</p>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">
                  {filteredEtudiants.filter(e => (e as any).status === 'incomplete').length}
                </p>
              </div>
            </Card>
            <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
              <div className="text-center">
                <p className="text-sm text-red-600 dark:text-red-400">Absents</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-200">
                  {filteredEtudiants.filter(e => (e as any).status === 'absent').length}
                </p>
              </div>
            </Card>
          </div>

          {/* Liste des étudiants */}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Matricule</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nom Complet</th>
                    {selectedType === 'examens' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code Copie</th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Notes</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Moyenne</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Statut</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700">
                  {filteredEtudiants.map((etudiant) => (
                    <tr key={etudiant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{etudiant.matricule}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{etudiant.nom}</span>
                          <span className="text-gray-600 dark:text-gray-400 ml-1">{etudiant.prenoms}</span>
                        </div>
                      </td>
                      {selectedType === 'examens' && (
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm">{etudiant.codeCopie}</span>
                        </td>
                      )}
                      <td className="px-4 py-3 text-center">
                        <div className="text-xs space-y-1">
                          {selectedType === 'devoirs' ? (
                            <>
                              <div>D1: {(etudiant as any).notes?.devoir1 || '-'}</div>
                              <div>D2: {(etudiant as any).notes?.devoir2 || '-'}</div>
                            </>
                          ) : (
                            <div>Ex: {(etudiant as any).notes?.examen || '-'}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-semibold">
                          {(etudiant as any).notes?.moyenne ? (etudiant as any).notes.moyenne.toFixed(2) : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge((etudiant as any).status)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            icon={<FileText size={14} />}
                            onClick={() => navigate(`/etudiants/${etudiant.id}`)}
                          >
                            Voir
                          </Button>
                          <Button 
                            variant="primary" 
                            size="sm" 
                            icon={<Printer size={14} />}
                            onClick={() => handlePrint('individual', etudiant.id)}
                          >
                            Imprimer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Informations */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
        <div className="flex items-start">
          <FileText className="text-blue-600 dark:text-blue-400 mr-3 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Informations sur la génération</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <li>• Les relevés de devoirs sont générés pour tous les étudiants de l'option/niveau</li>
              <li>• Les relevés d'examens sont générés uniquement pour les copies scannées</li>
              <li>• Les formats disponibles: PDF (impression) et Excel (export)</li>
              <li>• Les relevés incluent tous les calculs selon le système LMD</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GenerationReleves;
